import { createAsyncThunk } from "@reduxjs/toolkit";

import { ACTIVITY_TYPES } from "Constants/constants";
import logger from "Libs/logger";
import { isJson } from "Libs/utils";
import { loadEnvironments } from "Reducers/environment";
import { AsyncThunkOptionType } from "Reducers/types";
import { AppDispatch } from "Store/configureStore";
import { getEnvironmentActivitiesRequest } from "Api/activity/getEnvironmentActivities.request";

import { LoadEnvironmentActivities } from "../types";

import type { Activity } from "Api/activity/types";
import { getActivityTypes } from "../activity.slice";

const checkDeletedEnvironments = (
  dispatch: AppDispatch,
  activities: Activity[],
  organizationId: string,
  projectId: string
) => {
  if (
    activities.some(
      activity => activity.type === ACTIVITY_TYPES.ENVIRONMENT.DELETE
    )
  ) {
    dispatch(
      loadEnvironments({
        organizationId,
        projectId
      })
    );
  }
};
export const loadEnvironmentStagedActivities = createAsyncThunk<
  {
    activities: Activity[];
    hasMore?: boolean;
    organizationDescriptionId: string;
    environmentDescriptionId: string;
    projectDescriptionId: string;
    environmentActivity: string[];
  },
  LoadEnvironmentActivities,
  AsyncThunkOptionType
>(
  "app/activity/environment/staged/load",
  async (
    {
      projectDescriptionId,
      environmentDescriptionId,
      organizationDescriptionId,
      filterNames
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const filters = getActivityTypes(filterNames);

      if (!filters.length) {
        return {
          activities: [],
          hasMore: false,
          organizationDescriptionId,
          environmentDescriptionId,
          projectDescriptionId,
          environmentActivity: []
        };
      }

      const encodedEnvId = encodeURIComponent(environmentDescriptionId);

      const activities = await getEnvironmentActivitiesRequest({
        projectId: projectDescriptionId,
        encodedEnvId,
        filters,
        state: ["staged"]
      });
      const ACTIVITY_PAGE_SIZE = 10;
      if (activities.length > ACTIVITY_PAGE_SIZE - 1) {
        activities.pop();
      }

      checkDeletedEnvironments(
        dispatch,
        activities,
        organizationDescriptionId,
        projectDescriptionId
      );

      const environmentActivity = activities.map(activity => activity.id);
      return {
        activities,
        organizationDescriptionId,
        environmentDescriptionId,
        projectDescriptionId,
        environmentActivity
      };
    } catch (err: any) {
      if (![404, 403].includes(err.code)) {
        const errorMessage = isJson(err)
          ? err
          : "An error occurred while attempting to load environment staged activities.";
        logger(errorMessage, {
          action: "environment_load_staged_activities",
          meta: {
            organizationDescriptionId,
            environmentDescriptionId,
            projectDescriptionId
          }
        });
      }

      return rejectWithValue(err);
    }
  }
);
